import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { PrimaryHeading, SenaryHeading } from 'components/Headings';
import Paragraph from 'components/Paragraph';
import { largeUp, extraLarge, mediumUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';

const MAX_DETAILS_WIDTH = 450;

import { VARIATIONS, VARIATION_DIMENSIONS } from './constants';

const CollectionBannerDetails = ({ title, description, eyebrow, variation, isSideAligned }) => (
  <div
    className={classNames('CollectionBannerDetails', {
      'CollectionBannerDetails--condensed': variation === VARIATIONS.CONDENSED,
      'CollectionBannerDetails--side-aligned': isSideAligned,
      'CollectionBannerDetails--centered': !isSideAligned,
    })}
  >
    {eyebrow && variation !== VARIATIONS.CONTROL && (
      <div className="CollectionBannerDetails__eyebrow">
        <SenaryHeading>{eyebrow}</SenaryHeading>
      </div>
    )}
    <div className="CollectionBannerDetails__title-container">
      <PrimaryHeading
        className={classNames('CollectionBannerDetails__title', {
          'CollectionBannerDetails__title--control': variation === VARIATIONS.CONTROL,
        })}
      >
        {title}
      </PrimaryHeading>
    </div>
    <Paragraph
      className={classNames('CollectionBannerDetails__description', {
        'CollectionBannerDetails__description--control': variation === VARIATIONS.CONTROL,
      })}
    >
      {description}
    </Paragraph>
    <style jsx>
      {`
        .CollectionBannerDetails--side-aligned {
          justify-content: end;
          text-align: left;
          padding: ${Theme.get('spacing') * 4}px;
        }

        .CollectionBannerDetails--centered {
          padding: ${Theme.get('spacing') * 5}px ${Theme.get('spacing') * 6}px;
          justify-content: center;
        }

        .CollectionBannerDetails--condensed {
          padding: ${Theme.get('spacing') * 5}px ${Theme.get('spacing') * 4}px;
        }

        .CollectionBannerDetails__title-container {
          margin-bottom: ${Theme.get('spacing') * 2}px;
        }

        .CollectionBannerDetails__eyebrow {
          margin-bottom: ${Theme.get('spacing') * 2}px;
        }

        @media ${largeUp} {
          .CollectionBannerDetails {
            width: 100%;
            height: 100%;
            max-width: ${MAX_DETAILS_WIDTH}px;
            display: flex;
            flex-direction: column;
            margin: 0 auto;
          }

          .CollectionBannerDetails--centered {
            padding: ${Theme.get('spacing') * 3}px ${Theme.get('spacing') * 6}px;
          }

          .CollectionBannerDetails--side-aligned {
            justify-content: center;
            text-align: center;
            margin: 0 0 0 auto;
            padding-left: ${Theme.get('spacing') * 6}px;
          }
        }

        @media ${extraLarge} {
          .CollectionBannerDetails {
            padding: ${Theme.get('spacing') * 4}px ${Theme.get('spacing') * 8}px;
          }
        }
      `}
    </style>
    <style jsx global>
      {`
        .CollectionBannerDetails__eyebrow .typography--senary-heading {
          color: ${Theme.get('colors-white')};
        }

        .CollectionBannerDetails__title.typography--primary-heading,
        .CollectionBannerDetails__description.Paragraph {
          color: ${Theme.get('colors-white')};
        }

        .CollectionBannerDetails__title.typography--primary-heading {
          font-size: ${VARIATION_DIMENSIONS[variation].MOBILE_TITLE_FONT_SIZE}px;
        }

        .CollectionBannerDetails__description.Paragraph {
          margin-bottom: 0;
          font-size: ${VARIATION_DIMENSIONS[variation].MOBILE_DESC_FONT_SIZE}px;
        }

        @media ${mediumUp} {
          .CollectionBannerDetails__title.typography--primary-heading {
            font-size: ${Theme.get('font-size-extra-large-px')};
            line-height: 1.25;
          }

          .CollectionBannerDetails__description.Paragraph {
            font-size: ${Theme.get('font-size-px')};
          }
        }

        @media ${extraLarge} {
          .CollectionBannerDetails__title--control.typography--primary-heading {
            font-size: ${Theme.get('font-size-primary-heading-px')};
            line-height: 1.31;
          }
          .CollectionBannerDetails__description--control.Paragraph {
            font-size: ${Theme.get('font-size-large-px')};
          }
        }
      `}
    </style>
  </div>
);

CollectionBannerDetails.propTypes = {
  description: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  variation: PropTypes.string,
  eyebrow: PropTypes.string,
  isSideAligned: PropTypes.bool,
};

CollectionBannerDetails.defaultProps = {
  variation: VARIATIONS.CONTROL,
  eyebrow: '',
  isSideAligned: false,
};

export default CollectionBannerDetails;
